import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { ISetting } from '@shared/interfaces/setting.interface';
import { NumbersOnlyValidator } from '@shared/validators/numbersOnly.validators';
import { PersonalEmailAllowedValidator } from '@shared/validators/personalEmailAllowed.validators';

export class PhoneEmailPersonalValidator {
  /**
   * Creates a validator that dynamically manages phone, email, and work phone requirements.
   * Uses functions to get settings at runtime to avoid timing issues.
   * Supports independent toggles for each field.
   *
   * @param getSettings - Function that returns settings at validation time
   * @param getIsEmailRequired - Function that returns whether email is required at validation time (legacy support)
   * @param getPreventPersonalEmails - Optional function to check if personal emails should be blocked
   * @returns ValidatorFn
   */
  static DynamicRequired(
    getSettings: () => ISetting,
    getIsEmailRequired: () => boolean,
    getPreventPersonalEmails?: (relationshipValue: string) => boolean
  ): ValidatorFn {
    let isValidating = false;

    return (AC: AbstractControl): null => {
      // Prevent infinite loop
      if (isValidating) {
        return null;
      }

      const emailControl = AC.get('email');
      const phoneControl = AC.get('phone');
      const workPhoneControl = AC.get('work_phone');

      if (emailControl && phoneControl) {
        isValidating = true;

        const phoneValue = phoneControl.value
          ? String(phoneControl.value).trim()
          : '';
        const emailValue = emailControl.value
          ? String(emailControl.value).trim()
          : '';
        const workPhoneValue = workPhoneControl?.value
          ? String(workPhoneControl.value).trim()
          : '';

        // Get settings at runtime
        const settings = getSettings();
        const isEmailRequired = getIsEmailRequired();

        // Check independent toggles (new behavior)
        const isEmailRequiredByToggle = settings?.is_reference_email_required || isEmailRequired;
        const isMobileRequiredByToggle = settings?.is_reference_mobile_number_required || false;
        const isWorkPhoneRequiredByToggle = settings?.is_reference_work_number_required || false;
        const isOnePhoneRequiredByToggle =
          (settings?.is_reference_one_phone_required && settings?.is_work_phone_number_enabled) || false;

        // Build email validators array
        const buildEmailValidators = (includeRequired: boolean) => {
          const validators = [Validators.email];

          // Block personal emails if the global setting disables them,
          // or if the selected reference relationship has prevent_personal_emails enabled
          const relationshipValue = AC.get('relationship')?.value;
          const preventFromRelationship = getPreventPersonalEmails
            ? getPreventPersonalEmails(relationshipValue)
            : false;

          if (
            settings?.settings_json?.is_personal_emails_allowed === false ||
            preventFromRelationship
          ) {
            validators.push(PersonalEmailAllowedValidator.PersonalEmailAllowed);
          }

          if (includeRequired) {
            validators.push(Validators.required);
          }

          return validators;
        };

        // Independent toggle logic
        if (isEmailRequiredByToggle || isMobileRequiredByToggle || isWorkPhoneRequiredByToggle || isOnePhoneRequiredByToggle) {
          // At least one toggle is ON - use independent validation

          // Email validation
          emailControl.setValidators(buildEmailValidators(isEmailRequiredByToggle));

          if (isOnePhoneRequiredByToggle && !isMobileRequiredByToggle && !isWorkPhoneRequiredByToggle) {
            // At least one of mobile/work required — re-evaluated dynamically:
            // require both when both empty; filling either clears required on the other.
            const bothPhonesEmpty = !phoneValue && !workPhoneValue;

            const mobileValidators = [NumbersOnlyValidator.NumbersOnly];
            if (bothPhonesEmpty) {
              mobileValidators.push(Validators.required);
            }
            phoneControl.setValidators(mobileValidators);

            if (workPhoneControl) {
              workPhoneControl.setValidators(bothPhonesEmpty ? [Validators.required] : null);
            }
          } else {
            // Mobile phone validation
            const mobileValidators = [NumbersOnlyValidator.NumbersOnly];
            if (isMobileRequiredByToggle) {
              mobileValidators.push(Validators.required);
            }
            phoneControl.setValidators(mobileValidators);

            // Work phone validation (no NumbersOnly — accepts + and extensions)
            if (workPhoneControl) {
              const workPhoneValidators = [];
              if (isWorkPhoneRequiredByToggle) {
                workPhoneValidators.push(Validators.required);
              }
              workPhoneControl.setValidators(workPhoneValidators.length ? workPhoneValidators : null);
            }
          }
        } else {
          // All toggles are OFF - use legacy dynamic behavior (at least one required)
          if (phoneValue && !emailValue && !workPhoneValue) {
            // Phone has value, others optional
            emailControl.setValidators(buildEmailValidators(false));
            phoneControl.setValidators([
              NumbersOnlyValidator.NumbersOnly,
              Validators.required
            ]);
            if (workPhoneControl) {
              workPhoneControl.setValidators(null);
            }
          } else if (!phoneValue && emailValue && !workPhoneValue) {
            // Email has value, others optional
            emailControl.setValidators(buildEmailValidators(true));
            phoneControl.setValidators([NumbersOnlyValidator.NumbersOnly]);
            if (workPhoneControl) {
              workPhoneControl.setValidators(null);
            }
          } else if (!phoneValue && !emailValue && workPhoneValue) {
            // Work phone has value, others optional
            emailControl.setValidators(buildEmailValidators(false));
            phoneControl.setValidators([NumbersOnlyValidator.NumbersOnly]);
            if (workPhoneControl) {
              workPhoneControl.setValidators([Validators.required]);
            }
          } else if (!phoneValue && !emailValue && !workPhoneValue) {
            // All empty - at least one required
            emailControl.setValidators(buildEmailValidators(true));
            phoneControl.setValidators([
              NumbersOnlyValidator.NumbersOnly,
              Validators.required
            ]);
            if (workPhoneControl) {
              workPhoneControl.setValidators([Validators.required]);
            }
          } else {
            // At least one has value - others optional
            emailControl.setValidators(buildEmailValidators(!!emailValue));
            phoneControl.setValidators([NumbersOnlyValidator.NumbersOnly]);
            if (workPhoneControl) {
              workPhoneControl.setValidators(null);
            }
          }
        }

        emailControl.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false
        });

        phoneControl.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false
        });

        if (workPhoneControl) {
          workPhoneControl.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false
          });
        }

        isValidating = false;
      }

      return null;
    };
  }
}
